class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.abortController = null;
    this.searchRequestId = 0;

    // ---- Trending searches — edit these to match your store ----
    this.trendingSearches = [
      'Power Drills',
      'Angle Grinders',
      'Measuring Tape',
      'Tool Kits',
      'Safety Gloves',
      'Screwdriver Sets',
      'Work Lights',
      'Wrenches'
    ];

    this.debouncedOnChange = debounce(this.onChange.bind(this), 300);

    this.setupEventListeners();
  }

  // ─────────────────────────────────────────────
  // LOCAL STORAGE HELPERS — Recent Searches
  // ─────────────────────────────────────────────
  getRecentSearches() {
    try {
      return JSON.parse(localStorage.getItem('ps_recent_searches') || '[]');
    } catch (e) {
      return [];
    }
  }

  saveRecentSearch(term) {
    if (!term || !term.trim()) return;

    let recent = this.getRecentSearches();

    // Remove duplicates (case-insensitive), then prepend latest
    recent = recent.filter(
      (s) => s.toLowerCase() !== term.toLowerCase()
    );
    recent.unshift(term.trim());

    // Keep only the last 5
    recent = recent.slice(0, 5);

    try {
      localStorage.setItem('ps_recent_searches', JSON.stringify(recent));
    } catch (e) {
      // localStorage not available — silently skip
    }
  }

  clearRecentSearches() {
    try {
      localStorage.removeItem('ps_recent_searches');
    } catch (e) {}
  }

  // ─────────────────────────────────────────────
  // EMPTY-STATE PANEL (Recent + Trending)
  // ─────────────────────────────────────────────
  buildEmptyStateHTML() {
    const recent = this.getRecentSearches();

    const recentHTML =
      recent.length > 0
        ? `
        <div class="ps-empty__section">
          <div class="ps-empty__header">
            <h2 class="ps-empty__heading">Recent Searches</h2>
            <button type="button" class="ps-empty__clear-btn" id="ps-clear-recent">Clear</button>
          </div>
          <ul class="ps-empty__list">
            ${recent
              .map(
                (term) => `
              <li class="ps-empty__item">
                <button type="button" class="ps-empty__term-btn" data-search-term="${this.escapeAttr(term)}">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  ${this.escapeHTML(term)}
                </button>
              </li>`
              )
              .join('')}
          </ul>
        </div>`
        : '';

    const trendingHTML = `
      <div class="ps-empty__section">
        <h2 class="ps-empty__heading">Trending Searches</h2>
        <ul class="ps-empty__list ps-empty__list--pills">
          ${this.trendingSearches
            .map(
              (term) => `
            <li class="ps-empty__item">
              <button type="button" class="ps-empty__pill-btn" data-search-term="${this.escapeAttr(term)}">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
                ${this.escapeHTML(term)}
              </button>
            </li>`
            )
            .join('')}
        </ul>
      </div>`;

    return `
      <div id="predictive-search-empty-state" class="ps-empty">
        ${recentHTML}
        ${trendingHTML}
      </div>`;
  }

  renderEmptyState() {
    this.predictiveSearchResults.innerHTML = this.buildEmptyStateHTML();
    this.bindEmptyStateEvents();
    this.openPanel();
  }

  bindEmptyStateEvents() {
    // Term buttons (recent + trending) — fill the input and search
    this.predictiveSearchResults
      .querySelectorAll('[data-search-term]')
      .forEach((btn) => {
        btn.addEventListener('click', () => {
          const term = btn.getAttribute('data-search-term');
          this.input.value = term;
          this.getSearchResults(term);
        });
      });

    // Clear recent searches button
    const clearBtn = this.predictiveSearchResults.querySelector('#ps-clear-recent');

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        this.clearRecentSearches();
        this.renderEmptyState();
      });
    }
  }

  escapeHTML(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  escapeAttr(str) {
    return String(str).replace(/"/g, '&quot;');
  }

  // ─────────────────────────────────────────────
  // EVENT LISTENERS
  // ─────────────────────────────────────────────
  setupEventListeners() {
    const form = this.querySelector('form.search');

    if (form) {
      form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('search', this.onSearchClear.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onInput() {
    if (!this.getQuery().length) {
      this.close(false);
      this.renderEmptyState();
      return;
    }

    this.debouncedOnChange();
  }

  onSearchClear() {
    if (!this.getQuery().length) {
      this.close(false);
      this.renderEmptyState();
    }
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(false);
      this.renderEmptyState();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    const query = this.getQuery();

    if (!query.length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    // Save to recent searches when user submits the form
    this.saveRecentSearch(query);
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      // Show recent + trending when the field is empty
      this.renderEmptyState();
      return;
    }

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(false);
      this.renderEmptyState();
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;

      case 'Enter':
        this.selectOption();
        this.saveRecentSearch(this.getQuery());
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  // ─────────────────────────────────────────────
  // KEYBOARD NAVIGATION
  // ─────────────────────────────────────────────
  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allElements = Array.from(
      this.querySelectorAll('.predictive-search__list-item[role="option"]')
    );

    if (!allElements.length) return;

    const selectedIndex = selectedElement
      ? allElements.indexOf(selectedElement)
      : -1;

    const activeElement = moveUp
      ? allElements[(selectedIndex - 1 + allElements.length) % allElements.length]
      : allElements[(selectedIndex + 1) % allElements.length];

    if (activeElement === selectedElement) return;

    if (this.statusElement) {
      this.statusElement.textContent = '';
    }

    activeElement.setAttribute('aria-selected', 'true');

    if (selectedElement) {
      selectedElement.setAttribute('aria-selected', 'false');
    }

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedItem = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );

    if (selectedItem) {
      selectedItem.click();
    }
  }

  // ─────────────────────────────────────────────
  // FETCH
  // ─────────────────────────────────────────────
  cancelPendingRequest() {
    this.searchRequestId += 1;

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.toLowerCase().replace(/\s+/g, '-');

    this.cancelPendingRequest();

    const requestId = this.searchRequestId;

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      if (this.getQuery() === searchTerm) {
        this.renderSearchResults(this.cachedResults[queryKey]);
      }

      return;
    }

    this.abortController = new AbortController();

    const params = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product,collection',
      'resources[limit]': '4',
      'resources[limit_scope]': 'each',
      section_id: 'predictive-search'
    });

    const separator = routes.predictive_search_url.includes('?') ? '&' : '?';
    const requestUrl = `${routes.predictive_search_url}${separator}${params.toString()}`;

    fetch(requestUrl, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        if (requestId !== this.searchRequestId || this.getQuery() !== searchTerm) {
          return;
        }

        const parsedDocument = new DOMParser().parseFromString(text, 'text/html');
        const section = parsedDocument.querySelector('#shopify-section-predictive-search');

        if (!section) throw new Error('Predictive search section not found.');

        const resultsMarkup = section.innerHTML;

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error.name === 'AbortError' || requestId !== this.searchRequestId) return;
        this.close();
        console.error('Predictive search error:', error);
      })
      .finally(() => {
        if (requestId === this.searchRequestId) {
          this.abortController = null;
        }
      });
  }

  // ─────────────────────────────────────────────
  // LIVE REGION
  // ─────────────────────────────────────────────
  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setAttribute('loading', 'true');
    this.setLiveRegionText(this.loadingText);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText || '';

    setTimeout(() => {
      if (this.statusElement) {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }
    }, 1000);
  }

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────
  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', 'true');
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const countElement = this.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );

    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    }
  }

  // ─────────────────────────────────────────────
  // OPEN / CLOSE
  // ─────────────────────────────────────────────
  getResultsMaxHeight() {
    const header = document.getElementById('shopify-section-header');

    this.resultsMaxHeight = header
      ? window.innerHeight - header.getBoundingClientRect().bottom
      : window.innerHeight;

    return this.resultsMaxHeight;
  }

  openPanel() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;

    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  open() {
    this.openPanel();
  }

  close(clearInput = true) {
    this.cancelPendingRequest();

    if (clearInput) {
      this.input.value = '';
      this.predictiveSearchResults.innerHTML = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute('aria-selected', 'false');
    }

    this.removeAttribute('loading');
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');

    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

// ─────────────────────────────────────────────
// STYLES — injected once, scoped to predictive-search
// ─────────────────────────────────────────────
(function injectPredictiveSearchEmptyStyles() {
  if (document.getElementById('ps-empty-styles')) return;

  const style = document.createElement('style');
  style.id = 'ps-empty-styles';
  style.textContent = `
    .ps-empty {
      padding: 16px 20px 20px;
    }
    .ps-empty__section + .ps-empty__section {
      margin-top: 20px;
      padding-top: 20px;
      border-top: 1px solid #f0f0f0;
    }
    .ps-empty__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;
    }
    .ps-empty__heading {
      font-size: 1.1rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: #888;
      margin: 0 0 10px;
    }
    .ps-empty__header .ps-empty__heading {
      margin-bottom: 0;
    }
    .ps-empty__clear-btn {
      background: none;
      border: none;
      padding: 0;
      font-size: 1.2rem;
      color: #e0201f;
      cursor: pointer;
      text-decoration: underline;
    }
    .ps-empty__clear-btn:hover {
      color: #c51918;
    }
    .ps-empty__list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .ps-empty__list--pills {
      flex-direction: row;
      flex-wrap: wrap;
      gap: 8px;
    }
    .ps-empty__term-btn {
      display: flex;
      align-items: center;
      gap: 8px;
      background: none;
      border: none;
      padding: 6px 4px;
      width: 100%;
      text-align: left;
      font-size: 1.4rem;
      color: #333;
      cursor: pointer;
      border-radius: 4px;
      transition: background 0.15s ease, color 0.15s ease;
    }
    .ps-empty__term-btn:hover {
      background: #f5f5f5;
      color: #e0201f;
    }
    .ps-empty__term-btn svg {
      flex-shrink: 0;
      color: #aaa;
    }
    .ps-empty__pill-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: #f4f4f4;
      border: 1px solid #e2e2e2;
      border-radius: 999px;
      padding: 6px 14px;
      font-size: 1.3rem;
      color: #333;
      cursor: pointer;
      transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
      white-space: nowrap;
    }
    .ps-empty__pill-btn:hover {
      background: #fff0f0;
      border-color: #e0201f;
      color: #e0201f;
    }
    .ps-empty__pill-btn svg {
      flex-shrink: 0;
      color: #e0201f;
    }
  `;

  document.head.appendChild(style);
})();

customElements.define('predictive-search', PredictiveSearch);